<!--
  Standard ripple effect on native click

  =========================================

    (?) Programmatically execute the ripple effect

      ```template
        <ui-ripple
          ref="ripple"
        />
      ```

      ```script
      function() {
        this.ripple?.forcedExecution()
      }
      ```

      Heads-up: native click can be prevented (but still benefit of the
        programmatically execution) by providing a "UI.STATUS.MUTED" status

-->
<template>
  <slot />
  <div
    ref="ripple"
    v-bind="$attrs"
    :class="[
      'ui-ripple',
      `ui-ripple--${variant}`,
      `ui-ripple--${status}`,
    ]"
  />
</template>

<style lang="scss">
.ui-ripple {
  @include inset;

  position: absolute;
  overflow: hidden;

  &::after {
    @include inset;

    position: absolute;
    pointer-events: none;
    background-image:
      radial-gradient(
        circle,
        $black 10%,
        transparent 10%
      );
    background-repeat: no-repeat;
    background-position: 50%;
    opacity: 0;
    transition: transform 0.6s, opacity 0.6s;
    transform: scale(10);
  }

  /// Theming
  @each $name, $value in $palette-semantic {

    &--#{ $name }::after {
      background-image:
        radial-gradient(
          circle,
          #{ $value } 10%,
          transparent 10%
        );
    }
  }

  /// Status
  &--enabled::after,
  &--muted::after {
    @include virtual-content
  }

  &--disabled {
    cursor: initial;
  }

  &--forced-execution::after,
  &--enabled:active::after {
    transform: scale(0);
    opacity: 0.2;
    transition: transform 0s, opacity 0s;
  }
}
</style>

<script lang="ts">
import type { SlotsType } from 'vue'
import { delay, Maybe } from '~/utils/lib/core/prelude'
import type { STATUS, VARIANT } from '~/utils/lib/layout/ui'

const FORCED_EXEC_CLASSNAME = 'ui-ripple--forced-execution'

export default defineComponent({
  props: {
    status: {
      type: String as PropType<STATUS>,
      required: false,
      default: 'enabled' satisfies STATUS
    },
    variant: {
      type: String as PropType<VARIANT>,
      required: false,
      default: 'light' satisfies VARIANT
    },
  },
  slots: Object as SlotsType<{
    'default': {},
  }>,
  setup() {
    const refRipple = useTemplateRef<Element>('ripple')

    return {
      refRipple,
    }
  },
  methods: {
    forcedExecution() {
      Maybe.toMaybe( this.refRipple )
        .map(async (element) => {
          element.classList.add(FORCED_EXEC_CLASSNAME)
          await delay(0)
          element.classList.remove(FORCED_EXEC_CLASSNAME)
        })
    },
  }
})
</script>
